import React, { ReactNode } from 'react';
import { Field, Form } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import { I18n } from 'react-redux-i18n';
import TextInput from '../../../../common/inputs/TextInput';
import PasswordInput from '../../../../common/inputs/passwordInput/PasswordInput';
import Button from '../../../../common/Button';
import { validationMap } from '../../../../../network/api/types/compte/RequirementType';
import compteApi from '../../../../../network/api/compteApi';
import { notifyErrorWithMessage, notifySuccess } from '../../../../common/Notification';
import { FieldErrorDTO } from '../../../../../network/api/types/compte/FieldErrorDTO';
import { ErrorDTO } from '../../../../../network/api/types/compte/ErrorDTO';

type Props = {
  onClose: () => void
  setDirty: (dirty: boolean, callback?: () => void) => void
  setModalI18n: (title: string, subtitle: string) => void
  pseudo: string,
  fetchPseudo: () => void,
};

type State = {
};

type Errors = {
  pseudo?: string,
  currentPassword?: string,
  newPassword?: string,
  confirmPassword?: string,
};

type FormValues = Errors;

class ConnectionInformationForm extends React.Component<Props, State> {
  state: State = {
  };

  componentDidMount(): void {
    this.props.setModalI18n('HOME.CONFIRM_MODAL_TITLE', 'CONNECTION_INFORMATION.CONFIRM_CLOSE');
  }

  handleEdit = async (values: FormValues): Promise<{ [FORM_ERROR]: FieldErrorDTO[]}> => {
    try {
      let errors: Errors = this.validatePseudo(
        values.pseudo,
        {},
      );
      errors = this.validatePassword(
        values.currentPassword,
        values.newPassword,
        values.confirmPassword,
        errors,
      );
      const updatePseudo = !(values.pseudo === this.props.pseudo) && !errors.pseudo;
      const updatePassword = values.newPassword
        && !errors.currentPassword && !errors.newPassword && !errors.confirmPassword;
      let retErrors: FieldErrorDTO[] = [];
      if (!updatePseudo) {
        if (errors.pseudo) {
          retErrors.push({
            field: 'pseudo',
            message: errors.pseudo,
          });
        }
      }
      if (!updatePassword) {
        if (errors.currentPassword) {
          retErrors.push({
            field: 'currentPassword',
            message: errors.currentPassword,
          });
        }
        if (errors.newPassword) {
          retErrors.push({
            field: 'newPassword',
            message: errors.newPassword,
          });
        }
        if (errors.confirmPassword) {
          retErrors.push({
            field: 'confirmPassword',
            message: errors.confirmPassword,
          });
        }
      }
      if (!updatePseudo && !updatePassword) {
        return { [FORM_ERROR]: retErrors };
      }
      const promises: Promise<{ [FORM_ERROR]: FieldErrorDTO[] }>[] = [];
      if (updatePseudo) {
        promises.push(compteApi.changePseudo({ value: values.pseudo })
          .then(() => {
            notifySuccess(I18n.t('CONNECTION_INFORMATION.SUCCESS_ID'));
            this.props.fetchPseudo();
          }).catch((err) => {
            if (typeof err.then === 'function') {
              return err.then((e: ErrorDTO) => ({
                [FORM_ERROR]: {
                  ...e.fieldErrors[0],
                  field: 'pseudo',
                },
              }));
            }
            return null;
          }));
      }
      if (updatePassword) {
        promises.push(compteApi.changePassword({
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
          confirmPassword: values.confirmPassword,
        })
          .then(() => {
            notifySuccess(I18n.t('CONNECTION_INFORMATION.SUCCESS_PASSWORD'));
          }).catch((err) => {
            if (typeof err.then === 'function') {
              return err.then((e: ErrorDTO) => {
                if (e.fieldErrors && e.fieldErrors[0].objectName?.toLowerCase() === 'old_password') {
                  notifyErrorWithMessage(e.fieldErrors[0].message);
                  return { [FORM_ERROR]: { ...e.fieldErrors[0], field: 'currentPassword' } };
                }
                notifyErrorWithMessage(e.fieldErrors[0].message);
                return { [FORM_ERROR]: e.fieldErrors };
              });
            }
            return null;
          }));
      }
      return Promise.all(promises)
        .then((submitErrors) => {
          submitErrors.forEach((err) => {
            if (err && err[FORM_ERROR]) {
              retErrors = retErrors.concat(err[FORM_ERROR]);
            }
          });
          if (!retErrors.length) {
            this.props.setDirty(false);
            this.props.onClose();
          } else {
            this.props.setDirty(true);
          }
          return { [FORM_ERROR]: retErrors };
        });
    } catch (err) {
      return { [FORM_ERROR]: [] };
    }
  };

  validatePseudo = (pseudo: string, errors: Errors): Errors => {
    const retErrors = errors;
    if (!validationMap.pseudoPattern(pseudo)) {
      retErrors.pseudo = I18n.t('VALIDATION.PSEUDO.LENGTH');
    } else if (validationMap.pseudoNotIDNPattern(pseudo)) {
      retErrors.pseudo = I18n.t('VALIDATION.PSEUDO.IDN');
    } else if (validationMap.pseudoNumericPattern(pseudo)) {
      retErrors.pseudo = I18n.t('VALIDATION.PSEUDO.PATTERN');
    }
    return retErrors;
  };

  validatePassword = (
    currentPassword: string, newPassword: string, confirmPassword: string, errors: Errors,
  ): Errors => {
    if (!newPassword) return errors;
    const retErrors = errors;
    if (!currentPassword) {
      retErrors.currentPassword = I18n.t('VALIDATION.PASSWORD.CURRENT_EMPTY');
      return retErrors;
    }
    if (!validationMap.all(newPassword)) {
      retErrors.newPassword = I18n.t('VALIDATION.PASSWORD.UNFIT');
      return retErrors;
    }
    if (!confirmPassword) {
      retErrors.confirmPassword = I18n.t('VALIDATION.PASSWORD.CONFIRM_PASSWORD');
    } else if (newPassword !== confirmPassword) {
      retErrors.confirmPassword = I18n.t('VALIDATION.PASSWORD.CONFIRM_DIFFERENT');
    }
    return retErrors;
  };

  render(): ReactNode {
    return (
      <Form
        onSubmit={this.handleEdit}
        validateOnBlur
        initialValues={{
          pseudo: this.props.pseudo,
        }}
        render={({
          handleSubmit, form, submitError,
        }) => {
          const getErrorMessage = (fieldName: keyof FormValues): string => {
            if (submitError) {
              return submitError.find((error: FieldErrorDTO) => error.field === fieldName)?.message;
            }
            return '';
          };
          const clearErrorMessage = (fieldName: string) => {
            if (submitError) {
              const fieldPredicate = (error: FieldErrorDTO) => error.field === fieldName;
              if (submitError.some(fieldPredicate)) {
                submitError.splice(submitError.findIndex(fieldPredicate), 1);
              }
            }
          };
          return (
            <form
              className="connection-information-form"
              onSubmit={form.getState().dirty ? handleSubmit : this.props.onClose}
              onChange={() =>
                this.props.setDirty(form.getState().dirty)}
            >
              <Field
                name="pseudo"
                component={TextInput}
                placeholder={I18n.t('CONNECTION_INFORMATION.CONNECTION_ID')}
                type="text"
                customError={getErrorMessage('pseudo')}
                onChange={() => clearErrorMessage('pseudo')}
              />
              <Field
                name="currentPassword"
                component={PasswordInput}
                fieldType="password"
                placeholder={I18n.t('CONNECTION_INFORMATION.PASSWORD_CURRENT')}
                type="text"
                customError={getErrorMessage('currentPassword')}
                onChange={() => clearErrorMessage('currentPassword')}
              />
              <div className="field-container">
                <Field
                  name="newPassword"
                  component={PasswordInput}
                  fieldType="password"
                  placeholder={I18n.t('CONNECTION_INFORMATION.PASSWORD_NEW')}
                  type="text"
                  customError={getErrorMessage('newPassword')}
                  withHelper
                  onChange={() => clearErrorMessage('newPassword')}
                />
                <Field
                  name="confirmPassword"
                  component={PasswordInput}
                  fieldType="password"
                  placeholder={I18n.t('actions.CONFIRM')}
                  type="text"
                  customError={getErrorMessage('confirmPassword')}
                  onChange={() => clearErrorMessage('confirmPassword')}
                />
              </div>
              <div className="button-container">
                <Button
                  type="third"
                  onClick={this.props.onClose}
                  i18nKey="actions.CANCEL"
                />
                <Button
                  type="primary"
                  buttonType="submit"
                  i18nKey="actions.SAVE"
                />
              </div>
            </form>
          );
        }}
      />
    );
  }
}

export default ConnectionInformationForm;
